// @ts-nocheck
import * as turf from '@turf/turf';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { NavigationControl, Map as ReactMapGL, useMap } from 'react-map-gl/mapbox';
import { useDispatch, useSelector } from 'react-redux';
import { CustomThemeProvider } from 'sonata-react-components';

import { setMapBounds, setMapCenter, setMapFit, setMapZoom } from '@/store/actions/reportingMap.actions';
import { getAllZooms, getPositionFit } from '@/store/selectors/reportingMap.selectors';
import { MapPopupStyled, MapStyle } from './map.style';

function getCursor({ isDragging, isHovering }) {
  return isDragging ? 'grabbing' : isHovering ? 'pointer' : 'default';
}

// biome-ignore lint/suspicious/noShadowRestrictedNames: <explanation>
function Map({
  additionControls,
  defaultBounds,
  fitBoundsOptions,
  id,
  interactiveLayerPatterns = [],
  layers = [],
  mapStyle = 'streets',
  maxZoom = 20,
  minZoom = 0,
  movingMethod = 'flyTo',
  navigationControl,
  popupConfig,
  preserveDrawingBuffer = false
}) {
  const dispatch = useDispatch();
  const { [id]: map } = useMap();
  const mapContainer = useRef();

  const positionFit = useSelector(store => getPositionFit(store, id));
  const zooms = useSelector(getAllZooms);
  const [{ interactions, interactiveLayerIds, loaded, popup }, setState] = useState(() => ({
    interactions: {
      doubleClickZoom: true,
      dragPan: true,
      dragRotate: true,
      scrollZoom: true,
      touchRotate: true,
      touchZoom: true
    },
    interactiveLayerIds: [],
    loaded: false,
    popup: {
      itemId: null,
      lngLat: [],
      show: false
    }
  }));

  const handleMapLayersChange = () => {
    const currentMap = map.getMap();
    if (!loaded) {
      return;
    }
    const layerIds = currentMap.getStyle().layers.map(l => l.id);
    setState(prevState => ({
      ...prevState,
      interactiveLayerIds: layerIds.filter(layerId =>
        interactiveLayerPatterns.some(pattern => layerId.includes(pattern))
      )
    }));
  };

  useEffect(() => {
    if (map) {
      const resizer = new ResizeObserver(map.resize);
      resizer.observe(mapContainer.current);

      return () => {
        resizer.disconnect();
      };
    }
  }, [map, mapContainer]);

  useEffect(() => {
    if (map) {
      handleMapLayersChange();
    }
  }, [interactiveLayerPatterns, layers, loaded, map]);

  function getBoundsWithPolygon(polygon) {
    const featureToBBox = polygon.type === 'Feature' ? polygon : turf.feature(polygon);
    return turf.bbox(featureToBBox);
  }

  const handleMouseMove = event => {
    const currentItemId = popup.itemId;
    const newItemId = event.features?.[0]?.id || event.features?.[0]?.properties?.name;
    if (currentItemId !== newItemId) {
      setState(prevState => ({
        ...prevState,
        popup: {
          ...prevState.popup,
          itemId: newItemId || null,
          lngLat: event.lngLat,
          show: Boolean(newItemId)
        }
      }));
    }
  };

  const handleMouseLeave = () => {
    setState(prevState => ({
      ...prevState,
      popup: {
        show: false
      }
    }));
  };

  const getFilteredLayers = () => {
    const filteredLayers = {
      native: [],
      noNative: []
    };
    if (!layers) {
      return filteredLayers;
    }
    filteredLayers.native = layers.filter(layer => layer.native);
    filteredLayers.noNative = layers.filter(layer => !layer.native);
    return filteredLayers;
  };

  const handleMove = () => {
    const mapZoom = map.getZoom();
    if (mapZoom !== zooms[id]) {
      dispatch(setMapZoom(id, map.getZoom()));
      dispatch(setMapCenter(id, map.getCenter().toArray()));
      dispatch(setMapBounds(id, map.getBounds().toArray()));
      dispatch(setMapFit(id, null));
    }
  };

  const handleLoad = () => {
    setState(prevState => ({
      ...prevState,
      loaded: true
    }));
    handleMove();
  };

  let fitBounds = {};
  if (positionFit) {
    fitBounds = { fitBounds: positionFit };
    if (fitBoundsOptions) {
      fitBounds = {
        ...fitBounds,
        fitBoundsOptions
      };
    }
  }
  const filteredLayers = getFilteredLayers();

  const getMapStyle = styleSelected => {
    switch (styleSelected) {
      case 'dark':
        return 'mapbox://styles/taptap/cl78tjcu2000p14rptx9um21b';
      case 'light':
        return 'mapbox://styles/taptap/cl78y77dw000e15p2gw08kt1k';
      case 'streets':
        return 'mapbox://styles/taptap/cl78vetip001215mgd7w7yyuu';
      case 'satellite':
        return 'mapbox://styles/taptap/cl78wx651000d15qul16yyqi7';
      default:
        return 'mapbox://styles/taptap/cl78y77dw000e15p2gw08kt1k';
    }
  };

  return (
    <MapStyle className={`mapWrapper ${id}`} ref={mapContainer}>
      <ReactMapGL
        attributionControl={false}
        height="100%"
        id={id}
        initialViewState={{
          bounds: getBoundsWithPolygon(defaultBounds)
        }}
        maxZoom={maxZoom}
        minZoom={minZoom}
        preserveDrawingBuffer={preserveDrawingBuffer}
        width="100%"
        {...interactions}
        {...fitBounds}
        getCursor={getCursor}
        logoPosition="bottom-right"
        mapOptions={{
          logoPosition: 'bottom-right'
        }}
        mapStyle={getMapStyle(mapStyle)}
        mapboxAccessToken="pk.eyJ1IjoidGFwdGFwIiwiYSI6IjRSaHpHbUEifQ.2bAMMlWkzbPiVWA6dCE2Wg"
        movingMethod={movingMethod}
        onLoad={handleLoad}
        onMove={debounce(handleMove, 3000)}
        {...(popupConfig && {
          interactiveLayerIds: popupConfig?.layerIds || interactiveLayerIds,
          onMouseLeave: debounce(handleMouseLeave, popup.show ? 0 : 50),
          onMouseMove: handleMouseMove
        })}
      >
        {popup.show && popupConfig && (
          <CustomThemeProvider themeContrast={popupConfig.theme || 'dark'}>
            <MapPopupStyled
              className={popupConfig.className}
              closeButton={false}
              latitude={popup.lngLat.lat}
              longitude={popup.lngLat.lng}
            >
              <popupConfig.Component {...popupConfig.componentProps} itemId={popup.itemId} />
            </MapPopupStyled>
          </CustomThemeProvider>
        )}
        {filteredLayers.native.map((layer, key) => {
          const layerParams = layer.params || {};
          return <layer.Component key={`native${key}`} {...layerParams} mapId={id} />;
        })}
        {filteredLayers.noNative.map((layer, key) => {
          const layerParams = layer.params || {};
          return <layer.Component key={`${id}_noNative${key}_${mapStyle}`} {...layerParams} />;
        })}
        {navigationControl && <NavigationControl captureScroll position="bottom-right" showCompass={false} />}
      </ReactMapGL>
      {additionControls &&
        additionControls.map((control, i) => (
          <div
            className={`custom-control mapboxgl-ctrl ${control.position ? `mapboxgl-ctrl-${control.position}` : ''}`}
            key={`component_${i}`}
          >
            <control.Component {...control.componentProps} captureDrag captureScroll />
          </div>
        ))}
    </MapStyle>
  );
}

Map.propTypes = {
  additionControls: PropTypes.array,
  boundingBox: PropTypes.array,
  defaultBounds: PropTypes.any,
  fitBoundsOptions: PropTypes.object,
  id: PropTypes.string,
  interactiveLayerPatterns: PropTypes.arrayOf(PropTypes.string),
  layers: PropTypes.array,
  map: PropTypes.object,
  mapStyle: PropTypes.string,
  maxZoom: PropTypes.number,
  minZoom: PropTypes.number,
  movingMethod: PropTypes.oneOf(['jumpTo', 'easeTo', 'flyTo']),
  navigationControl: PropTypes.bool,
  popupConfig: PropTypes.object,
  preserveDrawingBuffer: PropTypes.bool
};

export default Map;
